import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { useSelector, } from 'react-redux';
import classNames from 'classnames';

import { PaymentSystem } from '@web-solutions/react-billing';

import Analytics from '@web-solutions/module-analytics';

import { Progress } from '@web-solutions/base-app/components/upsales';

import { selectPaymentSystem } from 'core/store/billing/selectors';

import { ProductDetails } from 'core/store/billing/selectors';

import { Modal } from 'core/ui-elements';
import { type Subscription } from 'core/interfaces/billing';
import type { PaymentModalTitle } from 'core/interfaces/payment-modal';

import { Options } from '..';

import BasePaymentModalBody from './body';
import BasePaddleModalBody from './paddle';

import { ReactComponent as IconClose } from './icons/close-icon.svg';

import classes from './style.module.scss';
import './style.scss';
import { useRemoteConfig } from 'core/hooks/use-remote-config';

interface Props {
  activeProduct: ProductDetails,
  /** The plan card the user picked. For Stripe `activeProduct` is the
   * subscription half of the pair, which carries the regular price but not the
   * card's own title/intro price — the plan description needs this one. */
  selectedProduct?: ProductDetails,
  onClose: () => void,
  onSuccess: (purchase: Subscription) => void,
  onError: (error: any) => void,
  onSubmit: (formData: any) => Promise<void>,
  showPopup: boolean,
  options: Options,
  showApplePay: boolean,
  showPayPal: boolean,
  showGooglePay: boolean,
  showMercadoPago: boolean,
  onApplePayClick: () => void,
  onApplePaySubmit: (formData: any) => Promise<void>,
  onGooglePayClick: () => void,
  onGooglePaySubmit: (formData: any) => Promise<void>,
  onPayPalSubmit: (formData: any) => Promise<void>,
  onPayPalSuccess: (p: Subscription) => void,
  onPayPalError: (e: any) => void,
  onUserInfoChange: (info: any) => void,
  isOnlyBaseModal?: boolean,
  fullMode?: boolean,
  title?: PaymentModalTitle,
  subtitle?: React.ReactNode,
  showCard?: boolean,
  isPurchaseProgress?: boolean,
  upsaleTrigger?:string,
  onSaveTokenOneTime?:(tokenId:string)=>void
  snowModalOncePopup?: boolean
}

const BasePaymentModal: React.FC<Props> = ({
  activeProduct,
  selectedProduct,
  onClose,
  onSuccess,
  onError,
  onSubmit,
  isOnlyBaseModal,
  showPopup,
  options,
  showApplePay,
  showPayPal,
  showGooglePay,
  showMercadoPago,
  showCard,
  onApplePayClick,
  onApplePaySubmit,
  onGooglePayClick,
  onGooglePaySubmit,
  onPayPalSubmit,
  onPayPalSuccess,
  onPayPalError,
  onUserInfoChange,
  fullMode,
  title,
  subtitle,
  isPurchaseProgress,
  upsaleTrigger,
  onSaveTokenOneTime,
  snowModalOncePopup
}) => {
  const paymentSystem = useSelector(selectPaymentSystem);
  //@ts-ignore
  const isPending = useSelector((state) => state.billing.pending || state.billing.orderPending);
  //@ts-ignore
  const isSubmitByCard = useSelector((state) => state.billing.isSubmitByCard);

  const ref = useRef<HTMLDivElement>(null);
const {newPaymentModal} = useRemoteConfig();
  useEffect(() => {
    if (isSubmitByCard) {
      ref.current?.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'start' });
    }
  }, [isSubmitByCard]);

  useEffect(() => {
    if (showPopup) {
      Analytics.trackEvent('modal_payment', 'open', {
        productId: activeProduct?.id,
        value: +activeProduct?.trialPriceAmount || +activeProduct?.amount,
        currency: activeProduct?.currency,
      });
      ref.current?.scrollIntoView(true);
    }
  }, [showPopup, activeProduct]);

  useLayoutEffect(() => {
    if (showPopup) {
      document.body.classList.add('ReactModal__Body--open');
    } else {
      document.body.classList.remove('ReactModal__Body--open');
    }
  }, [showPopup]);

  return (
    <Modal
      isOpen
      className={classNames(classes.modalContainer, { [classes.fullMode]: fullMode }, { [classes.modalContainer_active]: isPending }, { [classes.hidden]: !showPopup })}
      overlayClassName={classNames({ [classes.hidden]: !showPopup })}
    >
      <div className={classes.innerContainer} ref={ref}>
        {!newPaymentModal && <br />}
        <button type="button" onClick={onClose} className={classes.close}>
          <IconClose width="24px" height="24px" />
        </button>
        {isPurchaseProgress &&
          <div className={classes.process}>
            <Progress currentStep={'order'} />
          </div>
        }
        {paymentSystem === PaymentSystem.PADDLE
          ? (
            <BasePaddleModalBody
              subtitle={subtitle}
              activeProduct={activeProduct}
              options={options}
              onSubmit={onSubmit}
              onSuccess={onSuccess}
              onError={onError}
              onApplePayClick={onApplePayClick}
              onApplePaySubmit={onApplePaySubmit}
              onGooglePayClick={onGooglePayClick}
              onGooglePaySubmit={onGooglePaySubmit}
              onUserInfoChange={onUserInfoChange}
              onSaveTokenOneTime={onSaveTokenOneTime}
            />
          )
          : (
            <BasePaymentModalBody
              title={title}
              subtitle={subtitle}
              activeProduct={activeProduct}
              selectedProduct={selectedProduct}
              options={options}
              showApplePay={showApplePay}
              showPayPal={showPayPal}
              showGooglePay={showGooglePay}
              showMercadoPago={showMercadoPago}
              upsaleTrigger={upsaleTrigger}
              isOnlyBaseModal={isOnlyBaseModal}
              showCard={showCard}
              onSubmit={onSubmit}
              onSuccess={onSuccess}
              onError={onError}
              onApplePayClick={onApplePayClick}
              onApplePaySubmit={onApplePaySubmit}
              onGooglePayClick={onGooglePayClick}
              onGooglePaySubmit={onGooglePaySubmit}
              onPayPalSubmit={onPayPalSubmit}
              onPayPalSuccess={onPayPalSuccess}
              onPayPalError={onPayPalError}
              onUserInfoChange={onUserInfoChange}
              onSaveTokenOneTime={onSaveTokenOneTime}
              showModalPopup={snowModalOncePopup}
              isOpen={showPopup}
            />
          )}
      </div>
    </Modal>
  );
};

export default BasePaymentModal;
